import React, { useEffect, useState } from "react";
import axios from "../API/axios";
import { t } from "i18next";
import "../styles/recruiterFeatures.css";

const SCOPES = [
  { id: "jobs:read", labelKey: "Recruiter.ScopeJobs" },
  { id: "posts:read", labelKey: "Recruiter.ScopePosts" },
  { id: "applications:read", labelKey: "Recruiter.ScopeApplications" },
  { id: "pipeline:read", labelKey: "Recruiter.ScopePipeline" },
  { id: "applications:write", labelKey: "Recruiter.ScopeWriteBack" },
];

export default function CompanyAtsPanel({ compUid, isOwned }) {
  const sessionId = localStorage.getItem("sessionId");
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("ATS integration");
  const [scopes, setScopes] = useState(["jobs:read", "posts:read", "applications:read", "pipeline:read"]);
  const [createdKey, setCreatedKey] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [insights, setInsights] = useState([]);
  const [pipeline, setPipeline] = useState([]);

  const load = async () => {
    if (!compUid || !isOwned) return;
    try {
      const [keyRes, pipeRes, insightRes] = await Promise.all([
        axios.get(`CompanyAts/ListApiKeys?DeviceType=Web&CompUid=${encodeURIComponent(compUid)}`),
        axios.post("CompanyAts/Pipeline", { deviceType: "Web", sessionId, compUid }),
        axios.post("CompanyAts/JobInsights", { deviceType: "Web", sessionId, compUid }),
      ]);
      setKeys(keyRes.data?.responseData || []);
      setPipeline(pipeRes.data?.responseData || []);
      setInsights(insightRes.data?.responseData || []);
    } catch (e) {
      setError(e?.response?.data?.message || t("Recruiter.AtsLoadFailed"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compUid, isOwned]);

  if (!isOwned) return null;

  const toggleScope = (id) => {
    setScopes((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const createKey = async () => {
    setError("");
    setMessage("");
    setCreatedKey("");
    try {
      const res = await axios.post("CompanyAts/CreateApiKey", {
        deviceType: "Web",
        sessionId,
        compUid,
        name,
        scopes: scopes.join(","),
      });
      setCreatedKey(res.data?.responseData?.apiKey || res.data?.responseData?.ApiKey || "");
      setMessage(res.data?.message || t("Recruiter.ApiKeyCreated"));
      load();
    } catch (e) {
      setError(e?.response?.data?.message || t("Recruiter.ApiKeyCreateFailed"));
    }
  };

  const revoke = async (keyId) => {
    if (!window.confirm(t("Recruiter.RevokeConfirm"))) return;
    try {
      await axios.post("CompanyAts/RevokeApiKey", { deviceType: "Web", sessionId, compUid, keyId });
      setCreatedKey("");
      load();
    } catch (e) {
      setError(e?.response?.data?.message || t("Recruiter.RevokeFailed"));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.post(
        "CompanyAts/ExportApplications",
        { deviceType: "Web", sessionId, compUid },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `wlj-applications-${compUid}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.message || t("Recruiter.ExportFailed"));
    }
  };

  return (
    <div className="wlj-ats-panel">
      <div className="wlj-ats-panel__header">
        <p className="wlj-ats-panel__title">{t("Recruiter.AtsIntegration")}</p>
        <button type="button" className="wlj-ghost-btn" onClick={exportCsv}>
          {t("Recruiter.ExportCsv")}
        </button>
      </div>
      <p className="wlj-ats-panel__hint">
        {t("Recruiter.AtsHint")}
      </p>

      {pipeline.length > 0 && (
        <div className="wlj-ats-pipeline">
          {pipeline.map((s) => (
            <span key={s.hiringStatus ?? s.HiringStatus} className="wlj-status-chip wlj-status-chip--outline">
              {s.name || s.Name}: {s.count ?? s.Count}
              {(s.conversionFromApplied ?? s.ConversionFromApplied) > 0
                ? ` (${s.conversionFromApplied ?? s.ConversionFromApplied}%)`
                : ""}
            </span>
          ))}
        </div>
      )}

      <div className="mb-3">
        <label className="small label">{t("Recruiter.KeyName")}</label>
        <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="wlj-suggest-row">
          {SCOPES.map((s) => (
            <label key={s.id} className="small mb-0">
              <input
                type="checkbox"
                className="me-1"
                checked={scopes.includes(s.id)}
                onChange={() => toggleScope(s.id)}
              />
              {t(s.labelKey)}
            </label>
          ))}
        </div>
        <button type="button" className="mainBtn1 mt-2" onClick={createKey}>
          {t("Recruiter.CreateApiKey")}
        </button>
      </div>

      {createdKey && (
        <div className="alert alert-warning small">
          {t("Recruiter.CopyKeyNow")}
          <code className="d-block mt-1 user-select-all">{createdKey}</code>
        </div>
      )}
      {message && <div className="alert alert-success py-2 small">{message}</div>}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <ul className="list-unstyled mb-3">
        {keys.map((k) => (
          <li key={k.keyId || k.KeyId} className="wlj-ats-key-row">
            <span>
              <strong>{k.name || k.Name}</strong>{" "}
              <code>{k.keyPrefix || k.KeyPrefix}…</code>{" "}
              {(k.isActive ?? k.IsActive) ? (
                <span className="wlj-status-chip wlj-status-chip--ok">{t("Recruiter.Active")}</span>
              ) : (
                <span className="wlj-status-chip wlj-status-chip--muted">{t("Recruiter.Revoked")}</span>
              )}
            </span>
            {(k.isActive ?? k.IsActive) && (
              <button type="button" className="wlj-ghost-btn is-danger" onClick={() => revoke(k.keyId || k.KeyId)}>
                {t("Recruiter.Revoke")}
              </button>
            )}
          </li>
        ))}
      </ul>

      {insights.length > 0 && (
        <div>
          <p className="f-12 f-700">{t("Recruiter.JobInsights")}</p>
          {insights.slice(0, 8).map((j) => (
            <div key={j.jobId || j.JobId} className="wlj-ats-insight">
              <strong>{j.title || j.Title}</strong> — {j.applicants ?? j.Applicants} {t("Recruiter.Applicants")},{" "}
              {j.views ?? j.Views} {t("Recruiter.Views")}, {t("Recruiter.Hire")} {j.hireRate ?? j.HireRate}%
              {(j.avgDaysToHire ?? j.AvgDaysToHire) != null
                ? `, ${j.avgDaysToHire ?? j.AvgDaysToHire}d ${t("Recruiter.ToHire")}`
                : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
